import React, { useState } from 'react';
import { styled } from '@mui/material/styles'
import { Container, Button, Checkbox, FormControlLabel, Typography, Grid, Paper, TextField, Select, MenuItem } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload'
import { ComposedChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';
import * as tf from '@tensorflow/tfjs';

import { readCSV } from './utils/csvReader';
import { linearRegression } from '../lib/stats';
import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

function NeuralNetwork() {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [inputColumns, setInputColumns] = useState([]);
  const [outputColumn, setOutputColumn] = useState('');
  const [chartMin, setChartMin] = useState(0);
  const [chartMax, setChartMax] = useState(0);
  const [model, setModel] = useState(null);
  const [layerConfig, setLayerConfig] = useState([
    { units: 32, activation: 'relu' },
    { units: 32, activation: 'relu' },
    { units: 32, activation: 'relu' }
  ]);
  const [epochs, setEpochs] = useState(100);
  const [predictions, setPredictions] = useState([]);
  const [regression, setRegression] = useState(null);

  const loadCSV = async (file) => {
    const data = await readCSV(file);
    setData(data);
    setColumns(Object.keys(data[0]));
  };

  const trainModel = (data) => {
    const model = tf.sequential();
    layerConfig.forEach((layer, index) => {
      model.add(tf.layers.dense({
        units: layer.units,
        activation: layer.activation,
        inputShape: index === 0 ? [inputColumns.length] : undefined
      }));
    });
    model.add(tf.layers.dense({ units: 1 })); // Output layer
    model.compile({ optimizer: 'sgd', loss: 'meanSquaredError' });

    const inputs = data.map(row => inputColumns.map(col => parseFloat(row[col])));
    const labels = data.map(row => parseFloat(row[outputColumn]));

    const inputTensor = tf.tensor2d(inputs, [inputs.length, inputColumns.length]);
    const labelTensor = tf.tensor2d(labels, [labels.length, 1]);

    model.fit(inputTensor, labelTensor, {
      epochs,
      callbacks: {
        onEpochEnd: (epoch, logs) => console.log(`Epoch ${epoch}: loss = ${logs.loss}`)
      }
    }).then(() => {
      const predictions = model.predict(tf.tensor2d(inputs)).arraySync();
      const min = Math.min(...labels, ...predictions.flat());
      const max = Math.max(...labels, ...predictions.flat());
      setChartMin(parseFloat((min - .1).toFixed(1)));
      setChartMax(parseFloat((max + .1).toFixed(1)));
      const predictionPoints = predictions.map((pred, index) => ({
        x: labels[index],
        y: pred[0]
      }));
      const regression = linearRegression(predictionPoints);

      setPredictions(predictionPoints.map((point, index) => ({
        expected: point.x,
        predicted: point.y
      })));
      setRegression(regression);
      setModel(model);
    });
  };

  const [testInputs, setTestInputs] = useState({});
  const [testResult, setTestResult] = useState(null);

  const testModel = () => {
    if (!model) return;
    const inputData = inputColumns.map(col => parseFloat(testInputs[col] || 0));
    const prediction = model.predict(tf.tensor2d([inputData], [1, inputColumns.length]));
    prediction.array().then(result => setTestResult(result[0][0]));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center" >
        <Grid container justifyContent="center" xs={12} sx={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Neural Network Tool
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={8}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button fullWidth component="label" variant="contained" startIcon={<UploadIcon />}>
                  Upload file
                  <VisuallyHiddenInput type="file" onChange={(e) => loadCSV(e.target.files[0])} />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Select Input Columns</Typography>
                {columns.map(col => (
                  <FormControlLabel
                    key={col}
                    control={
                      <Checkbox
                        checked={inputColumns.includes(col)}
                        onChange={() => setInputColumns(prev => prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col])}
                      />
                    }
                    label={col}
                  />
                ))}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Select Output Column</Typography>
                <Select
                  fullWidth
                  value={outputColumn}
                  onChange={(e) => setOutputColumn(e.target.value)}
                >
                  {columns.map((col) => (
                    <MenuItem key={col} value={col}>
                      {col}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Configure Layers</Typography>
              </Grid>
              {layerConfig.map((layer, index) => (
                <>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label={`Units in Layer ${index + 1}`}
                      type="number"
                      value={layer.units}
                      onChange={(e) => {
                        const newConfig = [...layerConfig];
                        newConfig[index].units = parseInt(e.target.value, 10);
                        setLayerConfig(newConfig);
                      }}
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      label={`Activation for Layer ${index + 1}`}
                      value={layer.activation}
                      onChange={(e) => {
                        const newConfig = [...layerConfig];
                        newConfig[index].activation = e.target.value;
                        setLayerConfig(newConfig);
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        const newConfig = layerConfig.filter((_, i) => i !== index);
                        setLayerConfig(newConfig);
                      }}
                    >
                      Remove
                    </Button>
                  </Grid>
                </>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setLayerConfig([...layerConfig, { units: 10, activation: 'relu' }])}
                >
                  Add Layer
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Number of Epochs"
                  type="number"
                  value={epochs}
                  onChange={(e) => setEpochs(parseInt(e.target.value, 10))}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" color="primary" onClick={() => trainModel(data)}>Train Model</Button>
                <Button variant="contained" color="secondary" onClick={() => testModel()}>Test Model</Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {predictions.length > 0 && (
          <Grid item xs={8}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">Predictions</Typography>
                  <ResponsiveContainer width={600} height={600}>
                    <ComposedChart>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="expected" 
                        name="Expected" 
                        type="number"
                        label={{ value: 'Expected', position: 'insideBottomRight', offset: 0 }} 
                        domain={[chartMin, chartMax]}
                      />
                      <YAxis 
                        dataKey="predicted" 
                        name="Predicted" 
                        label={{ value: 'Predicted', angle: -90, position: 'insideLeft' }} 
                        type="number"
                        domain={[chartMin, chartMax]}
                      />
                      <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                      <Legend />
                      <Scatter name="Predictions" data={predictions} fill="#8884d8" />
                      {regression && (
                        <Line
                          type="monotone"
                          dataKey="predicted"
                          stroke="#FF0000"
                          data={[
                            { expected: chartMin, predicted: regression.m * chartMin + regression.b },
                            { expected: chartMax, predicted: regression.m * chartMax + regression.b }
                          ]}
                          name="Regression Line"
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
        {predictions.length > 0 && (
          <Grid item xs={8}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6">Test Model</Typography>
              <Grid container spacing={2}>
                {inputColumns.map(col => (
                  <Grid item xs={6} key={col}>
                    <TextField
                      fullWidth
                      label={`Value for ${col}`}
                      type="number"
                      value={testInputs[col] || ''}
                      onChange={(e) => setTestInputs(prev => ({ ...prev, [col]: e.target.value }))}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" onClick={testModel}>Test Model</Button>
                </Grid>
                {testResult !== null && (
                  <Grid item xs={12}>
                    <Typography variant="h6">Prediction Result: {testResult}</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}

export default function NeuralNetworkWrapper () {
  return <AutoSaveProvider>
    <NeuralNetwork />
  </AutoSaveProvider>
}
